import { useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';

// Tecton
import { useGetFcos } from '../../../api/fcos';
import { WorkspaceFCOContainer } from '../../../core/types/fcoTypes';
import { useUserSettings } from '../../context/UserSettingsContext';
import DataSourceOverview from './DataSourceOverview';
import { SourceRoutes, getSourceRouteName, getSourceRoutes } from './sourceUtils';

// @Tecton
import { Loading, ViewLayout } from '@lithos';
import FeatureViewsList from '../featureViews/FeatureViewsList';

const SourceContainerOutlet = () => {
  /*
    This contains the header portion of the Feature view
    - Breadcrumbs
    - Tabs
    - Outlet to the content
  */
  const navigation = useNavigate();
  const { name } = useParams();
  const { workspace } = useUserSettings();
  const location = useLocation();
  const featureRoutes = getSourceRouteName(location.pathname);
  const [selectedTab, setSelectedTab] = useState<SourceRoutes>(featureRoutes);

  const { data, isLoading } = useGetFcos(workspace ?? '', {
    select: (data: WorkspaceFCOContainer) => {
      return {
        dataSource: data?.dataSources.find((dataSource) => {
          return dataSource?.name === name;
        }),
      };
    },
  });

  const routeNames = Object.keys(getSourceRoutes());

  const headerProps = {
    breadcrumbs: {
      root: { label: workspace ?? '', url: `/repo/${workspace}` },
      items: [{ label: 'Sources', url: `/repo/${workspace}/data-sources` }, { label: name }],
    },
    title: name,
    subtitle: data.dataSource?.description ?? 'No description provided.',
    tabs: {
      items: routeNames.map((routeName) => ({ label: routeName, value: routeName })),
      selectedTab: selectedTab,
      setSelectedTab: (tab: SourceRoutes) => {
        const routes = getSourceRoutes();
        navigation(routes[tab]);
        setSelectedTab(tab);
      },
    },
  };

  if (isLoading) {
    return <ViewLayout header={headerProps} content={<Loading />} />;
  }

  return <ViewLayout header={headerProps} content={<Outlet />} />;
};

const SourceContainerContainer = () => {
  const { workspace } = useUserSettings();
  const { name } = useParams();
  const { data, isLoading } = useGetFcos(workspace ?? '', {
    select: (data: WorkspaceFCOContainer) => {
      return {
        dataSource: data?.dataSources.find((dataSource) => {
          return dataSource?.name === name;
        }),
        idToFcoMap: data?.idToFcoMap,
      };
    },
  });

  const dependentFeatureViews = data?.dataSource?.dependentFeatureViews?.map((id: string) => data.idToFcoMap[id]) ?? [];

  if (isLoading) {
    return <></>;
  }

  const sourceRoutes = getSourceRoutes();

  if (!data?.dataSource) {
    // We switched workspaces and didn't find the data source name, so we redirect them to the data source lists
    return <Navigate to={`../${workspace}/data-sources`} />;
  }

  return (
    <Routes>
      <Route element={<SourceContainerOutlet />}>
        <Route index element={<Navigate to={sourceRoutes[`Metadata`]} replace />} />
        <Route path={sourceRoutes[`Metadata`]} element={<DataSourceOverview source={data?.dataSource} />} />
        <Route
          path={sourceRoutes[`Feature Views`]}
          element={
            <FeatureViewsList items={dependentFeatureViews} idToFcoMap={data.idToFcoMap} workspace={workspace ?? ''} />
          }
        />
      </Route>
    </Routes>
  );
};

export default SourceContainerContainer;
